import React, { useState, useEffect, useCallback } from 'react';
import {
  Paper,
  Typography,
  Box,
  Button,
  Card,
  CardContent,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Chip,
  LinearProgress,
  Alert,
  AlertTitle,
  Tooltip,
  IconButton,
  Accordion,
  AccordionSummary,
  AccordionDetails,
} from '@mui/material';
import {
  Assessment,
  TrendingUp,
  Schedule,
  Business,
  Category,
  DateRange,
  PictureAsPdf,
  TableChart,
  BarChart,
  Timeline,
  Refresh,
  ExpandMore,
  Folder,
  Work,
  Assignment,
} from '@mui/icons-material';
import { useBrandColors } from '../hooks/useBrandColors';
import { apiService } from '../services/api';
import timezoneService from '../services/timezoneService';

interface TimeEntry {
  id: string;
  startTime: string;
  endTime: string;
  duration: number;
  description: string;
  task: string;
}

interface Task {
  name: string;
  hours: number;
  entries: number;
  timeEntries: TimeEntry[];
}

interface Project {
  name: string;
  hours: number;
  entries: number;
  tasks: Task[];
}

interface Client {
  name: string;
  hours: number;
  entries: number;
  projects: Project[];
}

interface TeamMember {
  id: string;
  name: string;
  email: string;
  hours: number;
  entries: number;
  percentage: number;
  topActivities: string[];
}

interface TeamData {
  totalMembers: number;
  totalTeamHours: number;
  members: TeamMember[];
}

interface TimelineDay {
  date: string;
  hours: number;
  projects: {
    name: string;
    hours: number;
    color: string;
  }[];
}

interface ReportData {
  summary: {
    totalHours: number;
    totalEntries: number;
    uniqueProjects: number;
    uniqueClients: number;
    averageHoursPerDay: number;
    timeRange: string;
    startDate: string;
    endDate: string;
    aiSummary?: string;
  };
  groupedData: any[];
  hierarchicalData?: Client[]; // NEW: Client→Project→Task hierarchy
  teamData?: TeamData; // NEW: Team member breakdown (only if > 1 member)
  timeline: TimelineDay[];
  teamTimeline?: TimelineDay[]; // NEW: Team timeline (only if > 1 member)
  rawEntries: any[];
}

export const Reports: React.FC = () => {
  const brandColors = useBrandColors();
  const [timeRange, setTimeRange] = useState('7d');
  const [groupBy, setGroupBy] = useState('hierarchical');
  const [reportData, setReportData] = useState<ReportData | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [timelineView, setTimelineView] = useState<'personal' | 'team'>('personal');

  const timeRangeOptions = [
    { value: '1d', label: 'Today' },
    { value: '7d', label: 'This Week' },
    { value: '30d', label: 'This Month' },
    { value: '90d', label: 'Last 3 Months' },
    { value: '1y', label: 'This Year' },
  ];

  const groupByOptions = [
    { value: 'hierarchical', label: 'Cards', icon: <Assessment /> },
    { value: 'project', label: 'Projects', icon: <Category /> },
    { value: 'client', label: 'Clients', icon: <Business /> },
    { value: 'category', label: 'Categories', icon: <BarChart /> },
    { value: 'date', label: 'Daily', icon: <DateRange /> },
  ];

  const loadReportData = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      console.log('📊 Loading report data...', { timeRange, groupBy });
      const data = await apiService.getReports(timeRange, groupBy);
      console.log('📊 Report data loaded:', data);
      setReportData(data);
    } catch (error) {
      console.error('❌ Failed to load report data:', error);
      setError(error instanceof Error ? error.message : 'Failed to load reports');
    } finally {
      setLoading(false);
    }
  }, [timeRange, groupBy]);

  useEffect(() => {
    loadReportData();
  }, [loadReportData]);

  const formatHours = (hours: number) => {
    const h = Math.floor(hours);
    const m = Math.round((hours - h) * 60);
    return h > 0 ? `${h}h ${m}m` : `${m}m`;
  };

  const formatDate = (dateString: string) => {
    // Convert UTC date string to user timezone and format
    const userDate = timezoneService.convertUTCToUserTime(dateString);
    return timezoneService.formatUserDate(userDate);
  };

  const getGroupByIcon = (type: string) => {
    const option = groupByOptions.find(opt => opt.value === type);
    return option?.icon || <Assessment />;
  };

  const exportToPDF = () => {
    // TODO: Implement PDF export
    alert('PDF export will be implemented in a future update');
  };

  const exportToCSV = () => {
    if (!reportData) return;
    
    const csvData = reportData.groupedData.map(item => ({
      Name: item.name,
      Hours: item.hours,
      Entries: item.entries,
      ...(item.client && { Client: item.client }),
      ...(item.category && { Category: item.category }),
    }));

    const csvContent = [
      Object.keys(csvData[0]).join(','),
      ...csvData.map(row => Object.values(row).join(','))
    ].join('\n');

    const blob = new Blob([csvContent], { type: 'text/csv' });
    const url = window.URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `time-tracker-report-${timeRange}-${groupBy}.csv`;
    a.click();
    window.URL.revokeObjectURL(url);
  };

  const styles = {
    container: {
      backgroundColor: 'white',
      minHeight: '100vh',
      padding: '24px'
    },
    header: {
      display: 'flex',
      alignItems: 'center',
      gap: '12px',
      marginBottom: '32px'
    },
    title: {
      fontSize: '28px',
      fontWeight: '600',
      color: '#1a1a1a'
    },
    subtitle: {
      fontSize: '16px',
      color: '#666',
      marginBottom: '24px'
    },
    summaryGrid: {
      display: 'grid',
      gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))',
      gap: '16px',
      marginBottom: '32px'
    },
    summaryCard: {
      textAlign: 'center',
      padding: '20px',
      borderRadius: '12px',
      boxShadow: '0 2px 8px rgba(0,0,0,0.1)'
    },
    summaryValue: {
      fontSize: '24px',
      fontWeight: '700',
      marginBottom: '8px'
    },
    summaryLabel: {
      fontSize: '14px',
      color: '#666'
    },
    controlsContainer: {
      display: 'flex',
      gap: '16px',
      marginBottom: '24px',
      flexWrap: 'wrap' as const,
      alignItems: 'center'
    },
    exportButtons: {
      display: 'flex',
      gap: '8px',
      marginLeft: 'auto'
    }
  };

  if (loading) {
    return (
      <div style={styles.container}>
        <div style={styles.header}>
          <Assessment sx={{ fontSize: '32px', color: brandColors.primary }} />
          <h1 style={{ ...styles.title, color: brandColors.textPrimary }}>Reports</h1>
        </div>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 3 }}>
          <LinearProgress sx={{ flex: 1 }} />
          <Typography variant="body2" color="text.secondary">
            Loading report data...
          </Typography>
        </Box>
      </div>
    );
  }

  if (error) {
    return (
      <div style={styles.container}>
        <div style={styles.header}>
          <Assessment sx={{ fontSize: '32px', color: brandColors.primary }} />
          <h1 style={{ ...styles.title, color: brandColors.textPrimary }}>Reports</h1>
        </div>
        <Alert severity="error" sx={{ mb: 3 }}>
          <AlertTitle>Error Loading Reports</AlertTitle>
          {error}
        </Alert>
        <Button
          variant="contained"
          startIcon={<Refresh />}
          onClick={loadReportData}
          sx={{ backgroundColor: brandColors.primary, '&:hover': { backgroundColor: brandColors.secondary } }}
        >
          Retry
        </Button>
      </div>
    );
  }

  if (!reportData) {
    return (
      <div style={styles.container}>
        <div style={styles.header}>
          <Assessment sx={{ fontSize: '32px', color: brandColors.primary }} />
          <h1 style={{ ...styles.title, color: brandColors.textPrimary }}>Reports</h1>
        </div>
        <Alert severity="info">
          <AlertTitle>No Data Available</AlertTitle>
          No time tracking data found for the selected period. Start tracking time to generate reports.
        </Alert>
      </div>
    );
  }

  return (
    <div style={styles.container}>
      {/* Header */}
      <div style={styles.header}>
        <Assessment sx={{ fontSize: '32px', color: brandColors.primary }} />
        <h1 style={{ ...styles.title, color: brandColors.textPrimary }}>Reports</h1>
      </div>
      
      {/* Subtitle and Controls on Same Row */}
      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 3, gap: 2, flexWrap: 'wrap' }}>
        <Typography variant="body1" sx={{ color: '#666', flex: 1, minWidth: '300px' }}>
          Comprehensive time tracking reports and analytics. Export data for further analysis or client billing.
        </Typography>
        
        <Box sx={{ display: 'flex', gap: 2, alignItems: 'center', flexWrap: 'wrap' }}>
        <FormControl size="small" sx={{ minWidth: 150 }}>
          <InputLabel>Time Range</InputLabel>
          <Select
            value={timeRange}
            onChange={(e) => setTimeRange(e.target.value)}
            label="Time Range"
            startAdornment={<Schedule sx={{ mr: 1, color: 'text.secondary' }} />}
          >
            {timeRangeOptions.map((option) => (
              <MenuItem key={option.value} value={option.value}>
                {option.label}
              </MenuItem>
            ))}
          </Select>
        </FormControl>

        <FormControl size="small" sx={{ minWidth: 150 }}>
          <InputLabel>Group By</InputLabel>
          <Select
            value={groupBy}
            onChange={(e) => setGroupBy(e.target.value)}
            label="Group By"
          >
            {groupByOptions.map((option) => (
              <MenuItem key={option.value} value={option.value}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                  {option.icon}
                  {option.label}
                </Box>
              </MenuItem>
            ))}
          </Select>
        </FormControl>

        <Button
          variant="outlined"
          startIcon={<Refresh />}
          onClick={loadReportData}
          disabled={loading}
          size="small"
        >
          Refresh
        </Button>

        <div style={styles.exportButtons}>
          <Tooltip title="Export to CSV">
            <IconButton
              onClick={exportToCSV}
              color="primary"
              size="small"
              sx={{ border: '1px solid #e0e0e0' }}
            >
              <TableChart />
            </IconButton>
          </Tooltip>
          <Tooltip title="Export to PDF (Coming Soon)">
            <span>
              <IconButton
                onClick={exportToPDF}
                color="primary"
                size="small"
                disabled
                sx={{ border: '1px solid #e0e0e0' }}
              >
                <PictureAsPdf />
              </IconButton>
            </span>
          </Tooltip>
        </div>
        </Box>
      </Box>

      {/* NEW: Smart Summary Cards - Only for Hierarchical View */}
      {groupBy === 'hierarchical' && reportData.hierarchicalData && (
        <Box sx={{ mb: 4 }}>
          {/* Period Summary Card */}
          <Paper sx={{ p: 3, mb: 3, borderRadius: 2, boxShadow: 3 }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 2 }}>
              <Assessment sx={{ fontSize: 32, color: brandColors.primary }} />
              <Typography variant="h6" sx={{ fontWeight: 600 }}>
                {timeRangeOptions.find(opt => opt.value === timeRange)?.label} - Summary
              </Typography>
            </Box>
            {/* AI Summary */}
            {reportData.summary.aiSummary && (
              <Box sx={{ mb: 2, p: 2, backgroundColor: `${brandColors.primary}08`, borderRadius: 1, borderLeft: `4px solid ${brandColors.primary}` }}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
                  <Typography variant="subtitle2" sx={{ fontWeight: 600, color: brandColors.primary }}>
                    🤖 AI Summary
                  </Typography>
                </Box>
                <Typography variant="body2" sx={{ color: 'text.primary', lineHeight: 1.6 }}>
                  {reportData.summary.aiSummary}
                </Typography>
              </Box>
            )}
            <Typography variant="body1" sx={{ mb: 2, color: 'text.secondary' }}>
              You tracked {formatHours(reportData.summary.totalHours)} across {reportData.summary.uniqueClients} client{reportData.summary.uniqueClients !== 1 ? 's' : ''} and {reportData.summary.uniqueProjects} project{reportData.summary.uniqueProjects !== 1 ? 's' : ''} during this period.
            </Typography>
            <Typography variant="subtitle2" sx={{ fontWeight: 600, mb: 1 }}>
              Main Activities:
            </Typography>
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.5 }}>
              {/* Calculate task distribution from hierarchical data */}
              {(() => {
                const taskTotals = new Map<string, number>();
                reportData.hierarchicalData.forEach(client => {
                  client.projects.forEach(project => {
                    project.tasks.forEach(task => {
                      taskTotals.set(task.name, (taskTotals.get(task.name) || 0) + task.hours);
                    });
                  });
                });
                const sortedTasks = Array.from(taskTotals.entries())
                  .sort((a, b) => b[1] - a[1])
                  .slice(0, 5);
                
                return sortedTasks.map(([taskName, hours]) => {
                  const percentage = (hours / reportData.summary.totalHours) * 100;
                  return (
                    <Box key={taskName} sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                      <Typography variant="body2">• {taskName}:</Typography>
                      <Typography variant="body2" sx={{ fontWeight: 500 }}>
                        {formatHours(hours)}
                      </Typography>
                      <Typography variant="caption" color="text.secondary">
                        ({percentage.toFixed(0)}%)
                      </Typography>
                    </Box>
                  );
                });
              })()}
            </Box>
          </Paper>

          {/* Client Cards Grid */}
          <Box sx={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(250px, 1fr))', gap: 2, mb: 3 }}>
            {reportData.hierarchicalData.map((client, idx) => (
              <Card key={idx} sx={{ 
                p: 2, 
                borderRadius: 2, 
                boxShadow: 2,
                '&:hover': { boxShadow: 4, transform: 'translateY(-2px)', transition: 'all 0.2s' }
              }}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
                  <Business sx={{ color: brandColors.primary }} />
                  <Typography variant="h6" sx={{ fontWeight: 600 }}>
                    {client.name}
                  </Typography>
                </Box>
                <Typography variant="h5" sx={{ fontWeight: 700, color: brandColors.primary, mb: 0.5 }}>
                  {formatHours(client.hours)}
                </Typography>
                <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
                  {client.entries} entries
                </Typography>
                <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                  {client.projects.slice(0, 3).map((project, pIdx) => (
                    <Chip 
                      key={pIdx}
                      label={project.name}
                      size="small"
                      variant="outlined"
                      sx={{ fontSize: '0.75rem' }}
                    />
                  ))}
                  {client.projects.length > 3 && (
                    <Chip 
                      label={`+${client.projects.length - 3}`}
                      size="small"
                      color="secondary"
                      sx={{ fontSize: '0.75rem' }}
                    />
                  )}
                </Box>
              </Card>
            ))}
          </Box>

          {/* Top Projects Card */}
          <Paper sx={{ p: 3, mb: 3, borderRadius: 2, boxShadow: 3 }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 2 }}>
              <TrendingUp sx={{ fontSize: 32, color: brandColors.secondary }} />
              <Typography variant="h6" sx={{ fontWeight: 600 }}>
                Top Projects
              </Typography>
            </Box>
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
              {(() => {
                // Get all projects across all clients
                const allProjects: Array<{ name: string; hours: number; tasks: string[] }> = [];
                reportData.hierarchicalData.forEach(client => {
                  client.projects.forEach(project => {
                    allProjects.push({
                      name: project.name,
                      hours: project.hours,
                      tasks: project.tasks.map(t => t.name)
                    });
                  });
                });
                
                return allProjects
                  .sort((a, b) => b.hours - a.hours)
                  .slice(0, 5)
                  .map((project, idx) => (
                    <Box key={idx} sx={{ 
                      display: 'flex', 
                      alignItems: 'center', 
                      gap: 2,
                      p: 1.5,
                      backgroundColor: idx === 0 ? `${brandColors.accent}15` : 'transparent',
                      borderRadius: 1
                    }}>
                      <Typography variant="h6" sx={{ fontWeight: 700, color: brandColors.accent, minWidth: 30 }}>
                        {idx + 1}.
                      </Typography>
                      <Box sx={{ flex: 1 }}>
                        <Typography variant="subtitle1" sx={{ fontWeight: 600 }}>
                          {project.name}
                        </Typography>
                        <Typography variant="caption" color="text.secondary">
                          {project.tasks.join(', ')}
                        </Typography>
                      </Box>
                      <Typography variant="subtitle1" sx={{ fontWeight: 600, color: brandColors.primary }}>
                        {formatHours(project.hours)}
                      </Typography>
                    </Box>
                  ));
              })()}
            </Box>
          </Paper>

          {/* Team Activity Card - Only if multiple members */}
          {reportData.teamData && reportData.teamData.totalMembers > 1 && (
            <Paper sx={{ p: 3, mb: 3, borderRadius: 2, boxShadow: 3, background: `linear-gradient(135deg, ${brandColors.primary}15 0%, ${brandColors.secondary}15 100%)` }}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 2 }}>
                <Work sx={{ fontSize: 32, color: brandColors.primary }} />
                <Typography variant="h6" sx={{ fontWeight: 600 }}>
                  Team Activity
                </Typography>
                <Chip 
                  label={`${reportData.teamData.totalMembers} members`}
                  size="small"
                  color="primary"
                />
              </Box>
              <Typography variant="body1" sx={{ mb: 2, color: 'text.secondary' }}>
                This {timeRangeOptions.find(opt => opt.value === timeRange)?.label.toLowerCase()} the team logged {formatHours(reportData.teamData.totalTeamHours)} across {reportData.summary.uniqueProjects} project{reportData.summary.uniqueProjects !== 1 ? 's' : ''}.
              </Typography>
              <Typography variant="subtitle2" sx={{ fontWeight: 600, mb: 2 }}>
                Top Contributors:
              </Typography>
              <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5 }}>
                {reportData.teamData.members.slice(0, 5).map((member, idx) => (
                  <Box key={member.id} sx={{ 
                    display: 'flex', 
                    alignItems: 'center', 
                    gap: 2,
                    p: 2,
                    backgroundColor: 'white',
                    borderRadius: 2,
                    boxShadow: 1
                  }}>
                    <Box sx={{ 
                      width: 40, 
                      height: 40, 
                      borderRadius: '50%', 
                      backgroundColor: idx === 0 ? brandColors.primary : brandColors.secondary,
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      color: 'white',
                      fontWeight: 700,
                      fontSize: '1.2rem'
                    }}>
                      {member.name.split(' ').map(n => n[0]).join('').toUpperCase().slice(0, 2)}
                    </Box>
                    <Box sx={{ flex: 1 }}>
                      <Typography variant="subtitle1" sx={{ fontWeight: 600 }}>
                        {member.name}
                      </Typography>
                      <Typography variant="caption" color="text.secondary">
                        {member.topActivities.slice(0, 3).join(', ')}
                      </Typography>
                    </Box>
                    <Box sx={{ textAlign: 'right' }}>
                      <Typography variant="h6" sx={{ fontWeight: 700, color: brandColors.primary }}>
                        {formatHours(member.hours)}
                      </Typography>
                      <Typography variant="caption" color="text.secondary">
                        {member.percentage}% of total
                      </Typography>
                    </Box>
                  </Box>
                ))}
              </Box>
            </Paper>
          )}
        </Box>
      )}

      {/* Hierarchical View (Client→Project→Task) */}
      {groupBy === 'hierarchical' && reportData.hierarchicalData && (
        <Paper sx={{ p: 3, mb: 3 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 3 }}>
            {getGroupByIcon(groupBy)}
            <Typography variant="h6" sx={{ fontWeight: 600 }}>
              Hierarchical Report (Client → Project → Task)
            </Typography>
            <Chip
              label={`${reportData.hierarchicalData.length} clients`}
              size="small"
              color="primary"
              variant="outlined"
            />
          </Box>
          
          {/* Client Accordions */}
          {reportData.hierarchicalData.map((client, clientIdx) => (
            <Accordion key={clientIdx} sx={{ mb: 1 }}>
              <AccordionSummary expandIcon={<ExpandMore />}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, width: '100%' }}>
                  <Business sx={{ color: brandColors.primary }} />
                  <Typography variant="subtitle1" sx={{ fontWeight: 600, flex: 1 }}>
                    {client.name}
                  </Typography>
                  <Chip label={formatHours(client.hours)} size="small" color="primary" />
                  <Chip label={`${client.entries} entries`} size="small" variant="outlined" />
                </Box>
              </AccordionSummary>
              <AccordionDetails sx={{ pl: 4 }}>
                {/* Project Accordions */}
                {client.projects.map((project, projectIdx) => (
                  <Accordion key={projectIdx} sx={{ mb: 1 }}>
                    <AccordionSummary expandIcon={<ExpandMore />}>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, width: '100%' }}>
                        <Folder sx={{ color: brandColors.secondary }} />
                        <Typography variant="subtitle2" sx={{ fontWeight: 500, flex: 1 }}>
                          {project.name}
                        </Typography>
                        <Chip label={formatHours(project.hours)} size="small" color="secondary" />
                        <Chip label={`${project.entries} entries`} size="small" variant="outlined" />
                      </Box>
                    </AccordionSummary>
                    <AccordionDetails sx={{ pl: 4 }}>
                      {/* Task Accordions */}
                      {project.tasks.map((task, taskIdx) => (
                        <Accordion key={taskIdx} sx={{ mb: 1 }}>
                          <AccordionSummary expandIcon={<ExpandMore />}>
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, width: '100%' }}>
                              <Assignment sx={{ color: brandColors.accent }} />
                              <Typography variant="body2" sx={{ fontWeight: 500, flex: 1 }}>
                                {task.name}
                              </Typography>
                              <Chip label={formatHours(task.hours)} size="small" sx={{ backgroundColor: brandColors.accent, color: 'white' }} />
                              <Chip label={`${task.entries} entries`} size="small" variant="outlined" />
                            </Box>
                          </AccordionSummary>
                          <AccordionDetails sx={{ pl: 4 }}>
                            {/* Time Entries */}
                            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
                              {task.timeEntries.map((entry, entryIdx) => (
                                <Paper key={entryIdx} sx={{ p: 2, backgroundColor: '#f8f9fa' }}>
                                  <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'start', mb: 1 }}>
                                    <Box sx={{ flex: 1 }}>
                                      <Typography variant="body2" sx={{ fontWeight: 500, mb: 0.5 }}>
                                        {entry.description || 'No description'}
                                      </Typography>
                                      <Typography variant="caption" color="text.secondary">
                                        {formatDate(entry.startTime)} • {formatHours(entry.duration)}
                                      </Typography>
                                    </Box>
                                    <Chip 
                                      label={entry.task || 'General'} 
                                      size="small" 
                                      variant="outlined"
                                    />
                                  </Box>
                                </Paper>
                              ))}
                            </Box>
                          </AccordionDetails>
                        </Accordion>
                      ))}
                    </AccordionDetails>
                  </Accordion>
                ))}
              </AccordionDetails>
            </Accordion>
          ))}
        </Paper>
      )}

      {/* Main Report Table (for non-hierarchical views) */}
      {groupBy !== 'hierarchical' && (
        <Paper sx={{ p: 3, mb: 3 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 3 }}>
            {getGroupByIcon(groupBy)}
            <Typography variant="h6" sx={{ fontWeight: 600 }}>
              Time Distribution by {groupByOptions.find(opt => opt.value === groupBy)?.label}
            </Typography>
            <Chip
              label={`${reportData.groupedData.length} items`}
              size="small"
              color="primary"
              variant="outlined"
            />
          </Box>

        <TableContainer>
          <Table>
            <TableHead>
              <TableRow>
                <TableCell sx={{ fontWeight: 600 }}>
                  {groupBy === 'date' ? 'Date' : 'Name'}
                </TableCell>
                <TableCell sx={{ fontWeight: 600 }}>Hours</TableCell>
                <TableCell sx={{ fontWeight: 600 }}>Entries</TableCell>
                <TableCell sx={{ fontWeight: 600 }}>Percentage</TableCell>
                {groupBy === 'project' && (
                  <TableCell sx={{ fontWeight: 600 }}>Client</TableCell>
                )}
                {(groupBy === 'client' || groupBy === 'category') && (
                  <TableCell sx={{ fontWeight: 600 }}>Projects</TableCell>
                )}
              </TableRow>
            </TableHead>
            <TableBody>
              {reportData.groupedData.map((item, index) => {
                const percentage = reportData.summary.totalHours > 0 
                  ? (item.hours / reportData.summary.totalHours) * 100 
                  : 0;
                
                return (
                  <TableRow key={index}>
                    <TableCell>
                      <Typography variant="subtitle2" sx={{ fontWeight: 500 }}>
                        {groupBy === 'date' ? formatDate(item.date) : item.name}
                      </Typography>
                    </TableCell>
                    <TableCell>
                      <Typography variant="body2" sx={{ fontWeight: 500 }}>
                        {formatHours(item.hours)}
                      </Typography>
                    </TableCell>
                    <TableCell>{item.entries}</TableCell>
                    <TableCell>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                        <LinearProgress
                          variant="determinate"
                          value={percentage}
                          sx={{
                            width: 60,
                            height: 6,
                            borderRadius: 3,
                            backgroundColor: '#f0f0f0',
                            '& .MuiLinearProgress-bar': {
                              backgroundColor: brandColors.accent
                            }
                          }}
                        />
                        <Typography variant="caption" sx={{ minWidth: 35 }}>
                          {percentage.toFixed(1)}%
                        </Typography>
                      </Box>
                    </TableCell>
                    {groupBy === 'project' && (
                      <TableCell>
                        <Typography variant="body2" color="text.secondary">
                          {item.client || 'No client'}
                        </Typography>
                      </TableCell>
                    )}
                    {(groupBy === 'client' || groupBy === 'category') && (
                      <TableCell>
                        <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                          {item.projects?.slice(0, 3).map((project: string, idx: number) => (
                            <Chip
                              key={idx}
                              label={project}
                              size="small"
                              variant="outlined"
                              sx={{ fontSize: '0.75rem' }}
                            />
                          ))}
                          {item.projects?.length > 3 && (
                            <Chip
                              label={`+${item.projects.length - 3} more`}
                              size="small"
                              variant="outlined"
                              color="secondary"
                              sx={{ fontSize: '0.75rem' }}
                            />
                          )}
                        </Box>
                      </TableCell>
                    )}
                  </TableRow>
                );
              })}
              {reportData.groupedData.length === 0 && (
                <TableRow>
                  <TableCell colSpan={6} align="center" sx={{ py: 4 }}>
                    <Typography variant="body1" color="text.secondary">
                      No data available for the selected time range and grouping.
                    </Typography>
                  </TableCell>
                </TableRow>
              )}
            </TableBody>
          </Table>
        </TableContainer>
        </Paper>
      )}

      {/* Enhanced Timeline Chart with Team Toggle */}
      {reportData.timeline.length > 0 && (
        <Paper sx={{ p: 3, borderRadius: 2, boxShadow: 3 }}>
          {/* Header with Toggle */}
          <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 3, flexWrap: 'wrap', gap: 2 }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
              <Timeline sx={{ fontSize: 32, color: brandColors.primary }} />
              <Typography variant="h6" sx={{ fontWeight: 600 }}>
                Daily Hours Timeline
              </Typography>
              <Chip
                label={`${reportData.timeline.length} days`}
                size="small"
                color="primary"
                variant="outlined"
              />
            </Box>
            
            {/* Team/Personal Toggle - Only show if team data exists */}
            {reportData.teamData && reportData.teamData.totalMembers > 1 && (
              <Box sx={{ display: 'flex', gap: 0.5, backgroundColor: '#f5f5f5', borderRadius: 1, p: 0.5 }}>
                <Button
                  size="small"
                  variant={timelineView === 'personal' ? 'contained' : 'text'}
                  onClick={() => setTimelineView('personal')}
                  sx={{
                    minWidth: 80,
                    backgroundColor: timelineView === 'personal' ? brandColors.primary : 'transparent',
                    color: timelineView === 'personal' ? 'white' : 'text.secondary',
                    '&:hover': {
                      backgroundColor: timelineView === 'personal' ? brandColors.primary : '#e0e0e0'
                    }
                  }}
                >
                  👤 Me
                </Button>
                <Button
                  size="small"
                  variant={timelineView === 'team' ? 'contained' : 'text'}
                  onClick={() => setTimelineView('team')}
                  sx={{
                    minWidth: 80,
                    backgroundColor: timelineView === 'team' ? brandColors.primary : 'transparent',
                    color: timelineView === 'team' ? 'white' : 'text.secondary',
                    '&:hover': {
                      backgroundColor: timelineView === 'team' ? brandColors.primary : '#e0e0e0'
                    }
                  }}
                >
                  👥 Team
                </Button>
              </Box>
            )}
          </Box>

          {/* Timeline Chart */}
          <Box sx={{ overflowX: 'auto' }}>
            <Box sx={{ display: 'flex', gap: 1.5, minWidth: 600, height: 220, alignItems: 'end', p: 2, position: 'relative' }}>
              {/* 8h Goal Line */}
              <Box sx={{
                position: 'absolute',
                left: 0,
                right: 0,
                top: '25%',
                height: '1px',
                borderTop: '2px dashed #ddd',
                zIndex: 0
              }}>
                <Typography variant="caption" sx={{ position: 'absolute', right: 0, top: -12, color: '#999', fontSize: '0.7rem' }}>
                  8h goal
                </Typography>
              </Box>

              {/* Use teamTimeline when toggle is 'team', otherwise use personal timeline */}
              {(timelineView === 'team' && reportData.teamTimeline ? reportData.teamTimeline : reportData.timeline).map((day, index) => {
                const activeTimeline = timelineView === 'team' && reportData.teamTimeline ? reportData.teamTimeline : reportData.timeline;
                const maxHours = Math.max(...activeTimeline.map(d => d.hours), 8);
                const totalHeight = maxHours > 0 ? (day.hours / maxHours) * 160 : 0;
                const barWidth = Math.max(600 / reportData.timeline.length - 6, 24);
                const isAboveGoal = day.hours >= 8;
                
                return (
                  <Tooltip
                    key={index}
                    title={
                      <Box sx={{ p: 0.5 }}>
                        <Typography variant="subtitle2" sx={{ fontWeight: 600, mb: 0.5 }}>
                          {formatDate(day.date)}
                        </Typography>
                        <Typography variant="body2" sx={{ mb: 0.5 }}>
                          📊 Total: {formatHours(day.hours)}
                        </Typography>
                        {day.projects && day.projects.length > 0 && (
                          <Box sx={{ mt: 0.5, pt: 0.5, borderTop: '1px solid rgba(255,255,255,0.2)' }}>
                            {day.projects.map((project, pIdx) => (
                              <Box key={pIdx} sx={{ display: 'flex', alignItems: 'center', gap: 0.5, mb: 0.25 }}>
                                <Box sx={{ 
                                  width: 8, 
                                  height: 8, 
                                  borderRadius: '50%', 
                                  backgroundColor: project.color,
                                  flexShrink: 0
                                }} />
                                <Typography variant="caption" sx={{ fontSize: '0.7rem' }}>
                                  {project.name}: {formatHours(project.hours)}
                                </Typography>
                              </Box>
                            ))}
                          </Box>
                        )}
                        {isAboveGoal && (
                          <Typography variant="caption" sx={{ color: '#4caf50', display: 'block', mt: 0.5 }}>
                            ✅ Goal reached!
                          </Typography>
                        )}
                      </Box>
                    }
                    arrow
                    placement="top"
                  >
                    <Box
                      sx={{
                        width: barWidth,
                        display: 'flex',
                        flexDirection: 'column-reverse',
                        height: 160,
                        cursor: 'pointer',
                        transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
                        '&:hover': {
                          transform: 'scale(1.05)',
                          filter: 'brightness(1.1)'
                        }
                      }}
                    >
                      {day.projects && day.projects.length > 0 ? (
                        day.projects.map((project, pIdx) => {
                          const segmentHeight = maxHours > 0 ? (project.hours / maxHours) * 160 : 0;
                          return (
                            <Box
                              key={pIdx}
                              sx={{
                                height: Math.max(segmentHeight, 2),
                                backgroundColor: project.color,
                                borderTop: pIdx < day.projects.length - 1 ? '1px solid rgba(255,255,255,0.3)' : 'none',
                                borderRadius: pIdx === day.projects.length - 1 ? '8px 8px 0 0' : '0',
                                boxShadow: '0 1px 3px rgba(0,0,0,0.1)',
                                transition: 'all 0.3s',
                              }}
                            />
                          );
                        })
                      ) : (
                        <Box
                          sx={{
                            height: Math.max(totalHeight, 3),
                            backgroundColor: '#f0f0f0',
                            borderRadius: '8px 8px 0 0'
                          }}
                        />
                      )}
                    </Box>
                  </Tooltip>
                );
              })}
            </Box>

            {/* Date Labels */}
            <Box sx={{ display: 'flex', justifyContent: 'space-between', mt: 1, px: 2 }}>
              <Typography variant="caption" color="text.secondary" sx={{ fontWeight: 500 }}>
                {formatDate(reportData.timeline[0]?.date)}
              </Typography>
              <Typography variant="caption" color="text.secondary" sx={{ fontWeight: 500 }}>
                {formatDate(reportData.timeline[reportData.timeline.length - 1]?.date)}
              </Typography>
            </Box>
          </Box>

          {/* Stats under timeline */}
          <Box sx={{ display: 'flex', gap: 3, mt: 3, pt: 3, borderTop: '1px solid #e0e0e0', flexWrap: 'wrap' }}>
            <Box>
              <Typography variant="caption" color="text.secondary">
                Average
              </Typography>
              <Typography variant="h6" sx={{ fontWeight: 700, color: brandColors.primary }}>
                {formatHours(reportData.summary.averageHoursPerDay)}
              </Typography>
            </Box>
            <Box>
              <Typography variant="caption" color="text.secondary">
                Total
              </Typography>
              <Typography variant="h6" sx={{ fontWeight: 700, color: brandColors.primary }}>
                {formatHours(reportData.summary.totalHours)}
              </Typography>
            </Box>
            <Box>
              <Typography variant="caption" color="text.secondary">
                Peak Day
              </Typography>
              <Typography variant="h6" sx={{ fontWeight: 700, color: brandColors.accent }}>
                {formatHours(Math.max(...reportData.timeline.map(d => d.hours)))}
              </Typography>
            </Box>
            {reportData.summary.averageHoursPerDay >= 8 && (
              <Box>
                <Typography variant="caption" sx={{ color: '#4caf50' }}>
                  🎉 Above 8h goal!
                </Typography>
              </Box>
            )}
          </Box>
        </Paper>
      )}
    </div>
  );
};

export default Reports;
